import type { SQL, SQLWrapper } from "drizzle-orm";
import { Transaction } from "@electric-sql/pglite";
import * as Sentry from "@sentry/browser";

import { createLogger } from "@/logger";

const log = createLogger("db");

import { PgDialect } from "drizzle-orm/pg-core";

import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry.ts";
import { db } from "./db";

// Export a more inclusive type for Tx that can work with both Transaction and PgTransaction
export type Tx = Transaction;
type responseWithError = { rows: string[] | null; error?: boolean; message?: string };

const logInfoAndMarkDBAsFailed = async ({
  sql,
  params,
  name,
  error,
}: {
  sql: string;
  params: any[];
  name: string;
  error: Error;
}): Promise<void> => {
  const registry = BootstrapEventsRegistry.getInstance();
  const bootstrapRegistryHasEvents = await registry.getLatestEventDate("events");
  if (!bootstrapRegistryHasEvents) {
    await registry.clearAll();
  }

  const ignoredErrors = ["current transaction is aborted, commands ignored until end of transaction block"];
  const hasIgnoredError = ignoredErrors?.includes(error.message);
  if (!hasIgnoredError) {
    if (error.message) {
      console.error(`%c[Error][${error.message}]`, "color:yellow;");
    }

    Sentry.captureMessage(`Database marked as failed due to error in query [${name}]`, {
      extra: { sql, params, name, error },
    });
    log.error(JSON.stringify({ sql }, null, 2));
    log.error(JSON.stringify({ params }, null, 2));
    log.error(`Error executeQuery [${name}]:`);
  }
};

export async function executeQuery(query: SQL, name: string, tx?: Tx): Promise<responseWithError> {
  const { sql, params } = new PgDialect({}).sqlToQuery(query);

  try {
    return await (tx || db.$client).query(sql, params);
  } catch (e) {
    logInfoAndMarkDBAsFailed({ sql, params, name, error: e });

    return {
      rows: null,
      error: true,
      message: (e as Error).message,
    };
  }
}

export async function executeBuiltQuery<T>(query: SQLWrapper, name: string, tx?: Tx): Promise<T[]> {
  const response: responseWithError = await executeQuery(query.getSQL(), name, tx);
  if (response.error) {
    log.error(`Error executeQuery [${name}]:`, response.error);
    log.error({ error: response });
    return [];
  }
  return response.rows as T[];
}
